import { AuthHttpError, type RequestConfig } from "../types";

export interface HttpClientOptions {
  baseUrl: string;
  getAccessToken: () => Promise<string | null>;
  /** Force a refresh and return the new access token, or null if refresh failed. */
  refreshAccessToken: () => Promise<string | null>;
  /** Called when a request fails auth even after a refresh attempt (session is dead). */
  onAuthFailure: () => void;
  defaultTimeoutMs?: number;
}

function isBodyInit(body: unknown): body is BodyInit {
  return (
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    body instanceof URLSearchParams ||
    typeof body === "string"
  );
}


function joinUrl(baseUrl: string, path: string): string {
  // Absolute URL → return unchanged.
  if (/^[a-z][a-z\d+.-]*:\/\//i.test(path)) {
    return path;
  }

  const base = new URL(baseUrl);

  if (path.startsWith("/")) {
    // Root-relative path.
    base.pathname = path;
  } else {
    // Relative path.
    const basePath = base.pathname.replace(/\/+$/, "");
    const relativePath = path.replace(/^\/+/, "");
    base.pathname = `${basePath}/${relativePath}`;
  }

  return base.toString();
}


/**
 * Fetch wrapper used for every authenticated call. Applications call
 * `auth.client.request(...)` instead of `fetch()` directly so that token
 * injection, refresh, retry, and error shape stay consistent app-wide.
 */
export class HttpClient {
  constructor(private readonly opts: HttpClientOptions) { }

  async request<T = unknown>(path: string, config: RequestConfig = {}): Promise<T> {
    const { body, timeoutMs, skipAuth, skipAuthRetry, headers, signal, ...rest } = config;

    const controller = new AbortController();
    const timeout = setTimeout(
      () => controller.abort(new DOMException("Request timed out", "TimeoutError")),
      timeoutMs ?? this.opts.defaultTimeoutMs ?? 15_000
    );
    // Combine caller-supplied signal (e.g. for manual cancellation) with the timeout signal.
    const onCallerAbort = () => controller.abort(signal?.reason);
    signal?.addEventListener("abort", onCallerAbort);

    try {
      const response = await this.send(path, {
        body,
        headers,
        skipAuth,
        signal: controller.signal,
        ...rest,
      });

      if (response.status === 401 && !skipAuth && !skipAuthRetry) {
        const newToken = await this.opts.refreshAccessToken();
        if (!newToken) {
          this.opts.onAuthFailure();
          throw await this.toError(response);
        }
        const retryResponse = await this.send(path, {
          body,
          headers,
          skipAuth,
          signal: controller.signal,
          ...rest,
        });
        return this.parse<T>(retryResponse);
      }

      if (!response.ok) throw await this.toError(response);
      return this.parse<T>(response);
    } catch (err) {
      throw this.normalizeError(err);
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener("abort", onCallerAbort);
    }
  }

  private async send(
    path: string,
    config: Omit<RequestConfig, "timeoutMs" | "skipAuthRetry">
  ): Promise<Response> {
    const { body, skipAuth, headers, ...rest } = config;
    const finalHeaders = new Headers(headers);

    let finalBody: BodyInit | undefined;
    if (body !== undefined) {
      if (isBodyInit(body)) {
        finalBody = body;
      } else {
        finalBody = JSON.stringify(body);
        if (!finalHeaders.has("Content-Type")) finalHeaders.set("Content-Type", "application/json");
      }
    }

    if (!skipAuth) {
      const token = await this.opts.getAccessToken();
      if (token) finalHeaders.set("Authorization", `Bearer ${token}`);
    }

    return fetch(joinUrl(this.opts.baseUrl, path), {
      ...rest,
      headers: finalHeaders,
      body: finalBody,
    });
  }

  private async parse<T>(response: Response): Promise<T> {
    if (response.status === 204) return undefined as T;
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) return (await response.json()) as T;
    return (await response.text()) as unknown as T;
  }

  private async toError(response: Response): Promise<AuthHttpError> {
    let body: unknown;
    try {
      body = await response.clone().json();
    } catch {
      body = await response.text().catch(() => undefined);
    }
    const message =
      (typeof body === "object" && body !== null && "message" in body && String((body as { message: unknown }).message)) ||
      response.statusText ||
      "Request failed";
    return new AuthHttpError(message, { status: response.status, code: `HTTP_${response.status}`, body });
  }

  private normalizeError(err: unknown): AuthHttpError {
    if (err instanceof AuthHttpError) return err;
    if (err instanceof DOMException && err.name === "AbortError") {
      return new AuthHttpError("Request was cancelled", { status: null, code: "CANCELLED" });
    }
    if (err instanceof DOMException && err.name === "TimeoutError") {
      return new AuthHttpError("Request timed out", { status: null, code: "TIMEOUT" });
    }
    if (err instanceof Error) {
      return new AuthHttpError(err.message, { status: null, code: "NETWORK_ERROR" });
    }
    return new AuthHttpError("Unknown error", { status: null, code: "UNKNOWN" });
  }
}
